import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { noop, take } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { InquiryDocumentResponse } from '../../../../api';
import { BaseCoreState, baseInquiryActions, baseInquirySelectors } from '../../../_store';
import { BaseModalService } from '../../../shared/services/base-modal.service';
import { BaseButtonSize } from '../../../shared/components/button/base-button.component';
import { BaseFileExtension, BaseMimeType } from '../../../shared/enums/base-available-file-type.enum';
import { BaseInquiryDocumentToUploadInterface } from '../../../modules/inquiry/_interfaces/base-inquiry-document-to-upload.interface';
import { BaseFileDownloadService } from '../../../shared/services/base-file-download.service';

@Component({
  selector: 'base-inquiry-document-preview',
  templateUrl: './inquiry-document-preview.component.html',
})
export class BaseInquiryDocumentPreviewComponent {
  @ViewChild('documentViewerTemplate') documentViewerTemplate: ElementRef;
  @ViewChild('deleteDocumentModalTemplate') deleteDocumentModalTemplate: ElementRef;

  @Input() documentInfo?: InquiryDocumentResponse;
  @Input() documentToUploadInfo: BaseInquiryDocumentToUploadInterface | null = null;
  @Input() isExistingDocument: boolean = true;
  @Input() canDeleteDocument: boolean = true;
  @Input() inquiryId?: number;
  @Output() cleanDocumentToUpload: EventEmitter<void> = new EventEmitter<void>();

  activeDocumentSrc: Blob;
  documentViewerModalRef: NgbModalRef;
  deleteDocumentModalRef: NgbModalRef;

  buttonSize = BaseButtonSize;
  baseFileExtension = BaseFileExtension;
  baseMimeType = BaseMimeType;

  constructor(
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService,
    private fileDownloadService: BaseFileDownloadService
  ) {}

  get documentFileName(): string {
    return this.documentInfo?.originalFilename ?? this.documentToUploadInfo?.name ?? '';
  }

  get documentFileType(): string {
    return this.documentInfo?.type ?? this.documentToUploadInfo?.file?.type ?? '';
  }

  downloadDocument(openInModal = false): void {
    this.cleanDocumentViewer();

    if (this.documentInfo) {
      if (openInModal) {
        this.store
          .select(baseInquirySelectors.getDownloadedInquiryDocumentFile)
          .pipe(
            filter((state) => !!state.data),
            map((state) => state.data),
            take(1)
          )
          .subscribe((document) => {
            this.openDocumentViewer(document as Blob);
          });
      }

      this.store.dispatch(
        baseInquiryActions.downloadInquiryDocumentFile({
          payload: {
            document: this.documentInfo.id,
            documentType: this.documentInfo.type,
            inquiry: this.inquiryId as number,
            isPreview: openInModal,
          },
        })
      );
    } else {
      if (this.documentToUploadInfo) {
        if (openInModal) {
          this.openDocumentViewer(this.documentToUploadInfo.file);
        } else {
          this.fileDownloadService.triggerBrowserDownload(
            this.documentToUploadInfo.file,
            this.documentToUploadInfo.file.name
          );
        }
      }
    }
  }

  openDeleteDocumentModal(): void {
    if (!this.canDeleteDocument) {
      return;
    }

    if (this.isExistingDocument) {
      this.deleteDocumentModalRef = this.modalService.open(
        {
          headText: 'document.delete.title',
          bodyTemplate: this.deleteDocumentModalTemplate,
          okText: 'action.okDelete',
        },
        {
          size: 'lg',
          backdrop: 'static',
        }
      );

      this.deleteDocumentModalRef.result.then(() => {
        this.deleteDocument();
        this.isExistingDocument = false;
      }, noop);
    } else {
      this.cleanDocumentToUpload.emit();
    }
  }

  private openDocumentViewer(document: Blob | File): void {
    this.activeDocumentSrc = document;
    this.documentViewerModalRef = this.modalService.open(
      {
        bodyTemplate: this.documentViewerTemplate,
        cancelButtonDisplayed: false,
        okButtonDisplayed: false,
        hideHeader: true,
        hideFooter: true,
      },
      { size: 'xl' }
    );
    this.documentViewerModalRef.result.then(
      () => this.cleanDocumentViewer(),
      () => this.cleanDocumentViewer()
    );
  }

  private cleanDocumentViewer() {
    this.store.dispatch(baseInquiryActions.clearDownloadedInquiryDocumentFile());
  }

  private deleteDocument(): void {
    this.store.dispatch(
      baseInquiryActions.deleteInquiryDocument({
        payload: { inquiry: this.inquiryId as number, document: (this.documentInfo as InquiryDocumentResponse).id },
      })
    );
  }
}
